
import BottomSheet from '@/components/BottomSheet.vue'
import ColorTemperatureHelp from '@/components/ColorTemperatureHelp.vue'
import ProductTechnicalFilterChipLabel from '@/components/ProductTechnicalFilterChipLabel.vue'
import ProductTechnicalFilterPanel from '@/components/ProductTechnicalFilterPanel.vue'
import ProductTechnicalFilterRange from '@/components/ProductTechnicalFilterRange.vue'
import TechnicalFilterHelp from '@/components/TechnicalFilterHelp.vue'
import { colorTemperatureToneRanges } from '@/utils/colorTemperature'
import {
  TECHNICAL_FILTERS,
  clampRange,
  formatTechnicalValue,
  parseRangeQuery,
  rangeQueryValue,
  technicalFilterScale,
  technicalRangeShortSummary,
  technicalRangeSummary,
} from '@/utils/technicalFilters'

const ICONS = {
  watts: ['fal', 'bolt'],
  lumens: ['fal', 'lightbulb-on'],
}

const LIT_ICONS = {
  watts: ['fas', 'bolt'],
  lumens: ['fas', 'lightbulb-on'],
}

const MENU_GUTTER = 12

export default {
  name: 'ProductTechnicalFilters',
  components: {
    BottomSheet,
    ColorTemperatureHelp,
    ProductTechnicalFilterChipLabel,
    ProductTechnicalFilterPanel,
    ProductTechnicalFilterRange,
    TechnicalFilterHelp,
  },
  props: {
    ranges: {
      type: [Object, Array],
      default: () => ({}),
    },
    stacked: {
      type: Boolean,
      default: false,
    },
    mobile: {
      type: Boolean,
      default: false,
    },
    pending: {
      type: Object,
      default: () => ({}),
    },
    value: {
      type: Object,
      default: null,
    },
    sheetId: {
      type: String,
      default: 'technical-filter-sheet',
    },
    dark: {
      type: Boolean,
      default: false,
    },
    order: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      openKey: null,
      dragging: false,
      pointerInPopover: false,
      sheetOpen: false,
      sheetKey: null,
      sheetReturnFocus: null,
    }
  },
  computed: {
    isLocal() {
      return this.value !== null
    },
    source() {
      return this.isLocal ? this.value : this.$route.query
    },
    query() {
      return this.stacked ? { ...this.source, ...this.pending } : this.source
    },
    filters() {
      return TECHNICAL_FILTERS.map(filter => {
        const { range, step, bounds } = this.scale(filter)
        if (!bounds) return null

        const selected = parseRangeQuery(this.query[filter.query])
        const format = this.formatter(filter)

        return {
          ...filter,
          step,
          bounds,
          value: clampRange(selected, bounds),
          values: range.values,
          selected,
          active: !!selected,
          icon: ICONS[filter.key],
          summary: selected ? technicalRangeSummary(selected, format) : '',
        }
      }).filter(Boolean)
    },
    orphans() {
      return TECHNICAL_FILTERS.map(filter => {
        if (this.scale(filter).bounds) return null

        const selected = parseRangeQuery(this.source[filter.query])
        if (!selected) return null

        const reset = this.$t('components.productTechnicalFilters.reset')
        const label = this.$t(
          `components.productTechnicalFilters.${filter.i18n}`
        )

        return {
          ...filter,
          selected,
          resetLabel: `${reset} ${label}`,
          removed:
            this.stacked &&
            Object.prototype.hasOwnProperty.call(this.pending, filter.query),
          summary: technicalRangeSummary(selected, this.formatter(filter)),
        }
      }).filter(Boolean)
    },
    chips() {
      const keys = this.order || TECHNICAL_FILTERS.map(({ key }) => key)
      const chips = keys
        .map(key => {
          const filter = this.filters.find(item => item.key === key)
          if (filter) return this.toChip(filter)

          const orphan = this.orphans.find(item => item.key === key)
          return orphan ? this.toChip({ ...orphan, orphan: true }) : null
        })
        .filter(Boolean)

      return this.mobile
        ? [
            ...chips.filter(chip => chip.active),
            ...chips.filter(chip => !chip.active),
          ]
        : chips
    },
    sheetChip() {
      return this.chips.find(chip => chip.key === this.sheetKey) || null
    },
  },
  beforeDestroy() {
    this.watchPopoverPointer(false)
    if (process.client) window.removeEventListener('resize', this.realignMenu)
  },
  methods: {
    chipVariant(chip) {
      if (chip.active) return 'chip-active'

      return this.dark ? 'chip-dark' : 'chip'
    },
    isExplained(filter) {
      return ['watts', 'lumens'].includes(filter.key)
    },
    scale(filter) {
      return technicalFilterScale(this.ranges, filter)
    },
    formatter(filter) {
      return number => formatTechnicalValue(number, filter, this.$i18n.locale)
    },
    toChip(filter) {
      const name = this.$t(`components.productTechnicalFilters.${filter.i18n}`)
      const active = !!(filter.orphan || filter.active)
      const tone = active && !filter.orphan ? this.toneOf(filter) : null
      const selected = active ? filter.selected : null

      return {
        ...filter,
        active,
        litIcon: LIT_ICONS[filter.key],
        icon: ICONS[filter.key],
        tone,
        heading: this.$t(
          `components.productTechnicalFilters.${filter.i18n}Heading`
        ),
        removeLabel: this.$t('components.productTechnicalFilters.remove', {
          name,
        }),
        shortSummary: tone
          ? this.$t(`components.colorTemperature.tones.${tone.key}`)
          : selected
          ? technicalRangeShortSummary(selected, filter, this.$i18n.locale)
          : '',
      }
    },
    toneOf(filter) {
      if (filter.key !== 'color_temperature') return null

      const [low, high] = filter.value

      return (
        colorTemperatureToneRanges(filter.bounds).find(
          ({ from, to }) => from === low && to === high
        ) || null
      )
    },
    change(filter, range) {
      this.emitPending(
        filter,
        rangeQueryValue(range, filter.bounds, filter.step)
      )
    },
    apply(filter, range) {
      this.push(filter, rangeQueryValue(range, filter.bounds, filter.step))
    },
    reset(filter) {
      if (this.stacked) {
        this.keepFocusInGroup(filter)
        this.emitPending(filter, undefined)
        return
      }

      this.keepFocusOn(filter)
      this.push(filter, undefined)
    },
    keepFocusInGroup(filter) {
      const group = this.$el.querySelector(`[data-orphan="${filter.key}"]`)
      const heading = group && group.querySelector('[tabindex="-1"]')

      if (heading) heading.focus()
    },
    keepFocusOn(filter) {
      const chip =
        !filter.orphan && this.$el.querySelector(`[data-chip="${filter.key}"]`)
      const target = chip && chip.querySelector('.dropdown-toggle, .btn')

      if (target) target.focus()
      else this.$emit('focus-lost')
    },
    emitPending(filter, value) {
      this.$emit('change', { ...this.pending, [filter.query]: value })
    },
    push(filter, value) {
      const dropdown = this.$refs[`dropdown-${filter.key}`]
      if (dropdown && dropdown[0]) dropdown[0].hide(true)
      this.sheetOpen = false

      if ((this.source[filter.query] || undefined) === value) return

      if (this.isLocal) {
        const next = { ...this.value, [filter.query]: value }
        if (value === undefined) delete next[filter.query]
        this.$emit('input', next)
        return
      }

      this.$router.push({
        path: this.$route.path,
        query: {
          ...this.$route.query,
          page: undefined,
          [filter.query]: value,
        },
      })
    },
    openSheet(chip, event) {
      this.sheetReturnFocus = event.currentTarget
      this.sheetKey = chip.key
      this.sheetOpen = true
    },
    opened(chip) {
      this.openKey = chip.key
      this.watchPopoverPointer(true)
      this.$nextTick(() => this.alignMenu(chip.key))
      window.addEventListener('resize', this.realignMenu, { passive: true })
    },
    realignMenu() {
      if (this.openKey) this.alignMenu(this.openKey)
    },
    alignMenu(key) {
      const menu = this.$el.querySelector(`[data-chip="${key}"] .dropdown-menu`)
      if (!menu) return

      menu.style.marginLeft = ''
      const rect = menu.getBoundingClientRect()
      const zoom = rect.width / menu.offsetWidth
      if (!zoom) return

      const overflow = rect.right - window.innerWidth + MENU_GUTTER
      const room = rect.left - MENU_GUTTER
      const shift = Math.min(Math.max(overflow, 0), Math.max(room, 0))

      if (shift) menu.style.marginLeft = `${-shift / zoom}px`
    },
    endDrag() {
      setTimeout(() => {
        this.dragging = false
      })
    },
    keepOpen(bvEvent) {
      if (this.dragging || this.pointerInPopover) bvEvent.preventDefault()
      this.pointerInPopover = false
    },
    closed() {
      this.openKey = null
      this.dragging = false
      this.watchPopoverPointer(false)
      window.removeEventListener('resize', this.realignMenu)
    },
    trackPointer(event) {
      this.pointerInPopover = !!event.target.closest?.('.popover')
    },
    watchPopoverPointer(enabled) {
      if (!process.client) return

      const method = enabled ? 'addEventListener' : 'removeEventListener'
      document[method]('mousedown', this.trackPointer, true)
      document[method]('touchstart', this.trackPointer, true)
    },
  },
}
